import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IArea } from 'src/app/_models/area.vm';
import { IPeriod } from 'src/app/_models/period.vm';
import { ISystemLevel } from 'src/app/_models/systemLevel.vm';
import { ApiService } from 'src/app/_services/api.service';
import { SessionService } from 'src/app/_services/session.service';
import { StatusService } from 'src/app/_services/status.service';
import { GoogleAnalyticsService } from '../../_services/google-analytics.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { trackByAreaId, trackBySystemLevelId } from '../utils/track-by';
import { ErrorReportingService } from '../../_services/error-reporting.service';

@Component({
    selector: 'app-select-area',
    templateUrl: './select-area.component.html',
    styleUrls: ['./select-area.component.scss'],
    providers: [ApiService, SessionService],
    standalone: false
})
export class SelectAreaComponent implements OnChanges {
  private readonly errorReporting = inject(ErrorReportingService);

  public readonly trackByAreaId = trackByAreaId;
  public readonly trackBySystemLevelId = trackBySystemLevelId;

  // Ties every subscription below to this component's lifetime, so a response
  // that arrives after the user has navigated away is dropped instead of
  // writing into a view that no longer exists.
  private readonly destroyRef = inject(DestroyRef);


  @Output() public currentArea = new EventEmitter<IArea>();
  @Input() public periodSelected: IPeriod;

  public systemLevels: Array<ISystemLevel>;
  public systemLevelSelected: ISystemLevel;

  public areas: Array<IArea>;
  public areaSelected: IArea;
  public areasLoading: boolean = false;

  public practices: Array<IArea>;
  public unassignedPractices: Array<IArea>;
  public practiceSelected: IArea;
  public showingPractices: boolean = false;
  public practiceSearch: string;

  public showAreaChange: boolean = false;
  public areaSearch: string = null;
  
  constructor(
    private apiService: ApiService,
    private sessionService: SessionService,
    private router: Router,
    private route: ActivatedRoute,
    private googleAnalyticsService: GoogleAnalyticsService,
    private statusService: StatusService
  ) { }

  ngOnChanges(): void {
    this.paramAndSessionCheck(); 
  }

  paramAndSessionCheck(): void {
    let savedAreaId: number = null;
    // Check sessionStorage
    let session = JSON.parse(sessionStorage.getItem('cvdpArea'));
    if (session) {
      savedAreaId = session.AreaID;
    }
    // Check query params
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => { 
      params.area ? savedAreaId = +params.area : null;
    });
    // Get areas
    if (savedAreaId) {
      this.getArea(savedAreaId, this.periodSelected.TimePeriodID);
      // Hide area selector
      this.hideAreaSelector();
    } else {
      this.getSystemLevels(this.periodSelected.TimePeriodID);
    }
  }

  getSystemLevels(periodId: number, systemLevelId?: number): void {
    this.apiService.getSystemLevels(periodId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        // Filter out hidden system levels
        this.systemLevels = success.systemLevels.filter(level => level.IsVisible == 'Y');
        if (systemLevelId) {
          this.selectSystemLevel(systemLevelId);
        }
      },
      error => {
        this.errorReporting.report('Get system levels', error);
      }
    )
  }

  selectSystemLevel(systemLevelId: number): void {
    this.showingPractices = false;
    // Don't load practice level; load PCN level instead
    systemLevelId == 5 ? systemLevelId = 4 : null;
    // Find system level and set
    let systemLevelIndex = this.systemLevels.findIndex(level => level.SystemLevelID == systemLevelId);
    this.systemLevelSelected = this.systemLevels[systemLevelIndex];
    this.getAreas(this.periodSelected.TimePeriodID, this.systemLevelSelected.SystemLevelID);
  }

  getAreas(periodId: number, systemLevelId: number): void {
    this.areas = null;
    this.apiService.getAreas(periodId, systemLevelId, null).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        this.areas = success.areaList;
      },
      error => {
        this.errorReporting.report('Get areas', error);
      }
    )
  }

  getUnassignedAreas(periodId: number, systemLevelId?: number): void {
    this.areasLoading = true;
    this.apiService.getUnassignedAreas(periodId, systemLevelId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        this.practices = success.unassignedAreaList;
      },
      error => {
        this.errorReporting.report('Get unassigned areas', error);
      },
      () => {
        this.areasLoading = false;
      }
    )
  }

  selectArea(areaId: number): void {
    // Select area
    let areaIndex = this.areas.findIndex(area => area.AreaID == areaId);
    this.areaSelected = this.areas[areaIndex];
    // Save area
    this.saveArea(this.areaSelected);
  }

  getArea(areaId: number, periodId: number): void {
    this.areas = null;
    this.areasLoading = true;
    this.apiService.getArea(areaId, periodId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        // Select area
        this.areaSelected = success.areaDetails;
        // Save area
        this.saveArea(this.areaSelected);
      },
      error => {
        this.errorReporting.report('Get area', error);
      },
      () => {
        this.areasLoading = false;
      }
    )
  }

  saveArea(area: IArea): void {
    // Collect data for Google Analytics
    this.googleAnalyticsService.areafilterEvent("Area", "filter", "click", area.AreaCode, area.AreaID, area.AreaName, area.SystemLevelName);
    // If PCN selected, get child Practices
    if (area.SystemLevelName == 'PCN') {
      this.getPractices(this.periodSelected.TimePeriodID, area.AreaID);
    } else if (area.SystemLevelName !== 'Practice') {
      this.practices = null;
    }
    // Set selected practice to null
    this.practiceSelected = null;
    // Save to session
    this.sessionService.setSessionStorage('cvdpArea', area);
    // Add to route
    this.router.navigate([], { queryParams: { area: area.AreaID }, queryParamsHandling: 'merge' });
    // Emit to parent
    this.currentArea.emit(area);
    // Hide area selector
    this.hideAreaSelector();
  }

  changeArea(systemLevelId: number): void {
    this.showAreaChange = true;
    this.getSystemLevels(this.periodSelected.TimePeriodID, systemLevelId);
    // Change status
    this.statusService.showingChangeArea.next(this.showAreaChange);
  }

  selectPracticeLevel() {
    this.areas = null;
    this.showingPractices = true;
    this.systemLevelSelected = null;
    if (this.areaSelected && this.areaSelected.SystemLevelID == 4) {
      this.getPractices(this.periodSelected.TimePeriodID, this.areaSelected.AreaID);
    }
  }

  getPractices(periodId: number, parentAreaId: number): void {
    this.practices = null;
    this.areasLoading = true;
    this.apiService.getAreas(periodId, null, parentAreaId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        this.practices = success.areaList;
      },
      error => {
        this.errorReporting.report('Get practices', error);
      },
      () => {
        this.areasLoading = false;
      }
    )
  }

  selectPractice(areaId: number): void {
    // Select area
    let areaIndex = this.practices.findIndex(area => area.AreaID == areaId);
    this.practiceSelected = this.practices[areaIndex];
    this.areaSelected = this.practices[areaIndex];
    // Save area
    this.saveArea(this.areaSelected);
  }

  hideAreaSelector(): void {
    // Hide area selector
    this.showAreaChange = false;
    // Change status
    this.statusService.showingChangeArea.next(this.showAreaChange);
  }

  toggleHelpText(tooltip, helpText: string) {
    if (tooltip.isOpen()) {
      tooltip.close();
    } else {
      tooltip.open({helpText});
    }
  }

}
